import { App, Button, Flex, Input, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, Copy, PlusCircle, Trash2 } from 'lucide-react';
import { type Key, type ReactNode, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import MealItemForm from './meal-item.form';
import MealItemsFilter, { type MealItemsFiltersProps } from './meal-items.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { Ingredient, MealItem, MealItemIngredient } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
import { randomUUID } from '../../../utils/utils';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'MealItem deleted successfully';
const SUCCESS_CLONE_MESSAGE = 'MealItem cloned successfully';

interface MealItemsPageState {
  filters?: MealItemsFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const serializeSort = (sort: MealItemsPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: MealItemsPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: MealItemsPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | MealItemsPageState['filters']
      | MealItemsPageState['pagination']
      | MealItemsPageState['sort'];
  }
): MealItemsPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as MealItemsPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as MealItemsPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as MealItemsPageState['sort'],
      };
  }
};

const MealItemsPage = (): ReactNode => {
  const { modal } = App.useApp();
  const notification = useNotification();
  const { token } = theme.useToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const mealItemId = searchParams.get('id');
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [mealItemToEdit, setMealItemToEdit] = useState<Partial<MealItem>>();
  const mealItems = useQuery<MealItem[]>(
    debouncedState && [
      'meal-item',
      {
        include: [
          'mealItemIngredients.ingredient',
          'measure',
          'mealItemIngredients.measure',
          'servingMeasure',
          'servingTool',
        ],
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  const atomicOperations = useAtomicOperations();
  const mealItem = useQuery<MealItem>(
    mealItemId && [
      'meal-item',
      mealItemId,
      {
        include: [
          'mealItemIngredients.ingredient',
          'measure',
          'mealItemIngredients.measure',
          'servingMeasure',
          'servingTool',
        ],
      },
    ]
  );

  useEffect(() => {
    if (mealItem.data) {
      setMealItemToEdit(mealItem.data);
    }
  }, [mealItem.data]);

  useEffect(() => {
    mealItem.error &&
      notification.error({
        message: ' ',
        description: mealItem.error.detail,
      });
  }, [mealItem.error]);

  useEffect(() => {
    mealItems.error &&
      notification.error({
        message: ' ',
        description: mealItems.error.detail,
      });
  }, [mealItems.error]);
  const columns: DataTableProps<MealItem>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            searchParams.set('id', item?.id ?? '');
            setSearchParams(searchParams);
            setMealItemToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
  ];
  const cloneMealItem = async (MealItemToCopy: MealItem, name: string): Promise<void> => {
    const mealItemToBeSaved = new MealItem(
      undefined,
      name,
      MealItemToCopy.servingMeasure,
      MealItemToCopy.measure,
      MealItemToCopy.servingSize,
      [],
      MealItemToCopy.servingTool,
      MealItemToCopy.size
    );

    // @ts-expect-error operand of delete operator must be optional
    delete mealItemToBeSaved.mealItemIngredients;

    const mealItemOperation: AtomicOperation<MealItem> = AtomicOperation.add(
      mealItemToBeSaved,
      randomUUID()
    );

    const miis = MealItemToCopy.mealItemIngredients.map(
      (mii) =>
        new MealItemIngredient(
          undefined,
          new Ingredient(mii.ingredient.id),
          mii.units,
          mii.measure,
          new MealItem(mealItemOperation.lid),
          mii.sortOrder
        )
    );

    const mealItemIngredientsToAdd = miis
      .filter((mii) => !mii.id)
      .map((mii) =>
        AtomicOperation.add<MealItemIngredient>(mii, undefined, [
          'meal-item',
          mii.mealItem.id!,
          'mealItemIngredients',
          mii.id!,
        ])
      );

    atomicOperations
      .operate(
        Array.of<AtomicOperation<MealItem | MealItemIngredient>>(
          mealItemOperation,
          ...mealItemIngredientsToAdd
        )
      )
      .pipe(
        switchMap(([mi, ...miis]) =>
          of<MealItem>({
            ...(mi.data as MealItem),
            mealItemIngredients: miis
              .map((mii) => mii.data as MealItemIngredient)
              .filter((mii) => mii),
          })
        ),
        tap(() => {
          notification.success({
            message: ' ',
            description: SUCCESS_CLONE_MESSAGE,
          });
        }),
        tap(() => {
          mealItems.refetch?.();
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex gap="middle" vertical>
      <DataTable<MealItem>
        title="Meal items"
        description="List of meal items to construct meals"
        filtersNode={
          <MealItemsFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setMealItemToEdit({});
              }}
            >
              Add Meal Item
            </Button>
          </AuthorizedComponent>
        }
        dataList={mealItems}
        onChange={setState}
        columns={columns}
        currentPage={state.pagination.current}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              void modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(mealItems.client.delete(['meal-item', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({ message: ' ', description: SUCCESS_DELETE_MESSAGE });
                        mealItems.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete meal item',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
          {
            key: 'clone',
            label: 'Clone meal item',
            icon: <Copy />,
            onItemClick: (record) => {
              let tempInputValue = `${record.name} - clone`;
              void modal.confirm({
                width: '50%',
                title: ' Confirm Clone',
                icon: <CircleAlert />,
                content: (
                  <Flex vertical gap="small">
                    <Typography.Text>
                      Clone meal item <b>{record.name}</b> to
                    </Typography.Text>
                    <Input
                      style={{ width: '50%' }}
                      placeholder="Enter meal item name"
                      defaultValue={tempInputValue}
                      onChange={(e) => (tempInputValue = e.target.value)}
                    />
                  </Flex>
                ),
                okType: 'primary',
                onOk: async () => {
                  await cloneMealItem(record, tempInputValue);
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={mealItemToEdit}>
        <ExtraContent>
          <MealItemForm
            mealItem={mealItemToEdit ?? {}}
            onSuccess={(_mealItem, newMealItem) => {
              mealItems.refetch?.();
              if (newMealItem) setMealItemToEdit({});
              else setMealItemToEdit(undefined);
            }}
            onCancel={() => {
              searchParams.delete('id');
              setSearchParams(searchParams);
              setMealItemToEdit(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default MealItemsPage;
